import React, { memo, useLayoutEffect } from 'react';
import { useDocumentation } from '../../contexts/DocumentationContext';
import documentationData, { getSectionOverview } from '../../data/documentationData';
import CopyLinkButton from './CopyLinkButton';

// Breadcrumb component
const Breadcrumb = ({ section, subsectionId }) => {
  const { navigateToSection, navigateToSubsection } = useDocumentation();
  
  const currentSubsection = subsectionId
    ? section.subsections[subsectionId]
    : null;
    
  return (
    <div className="docs-breadcrumb">
      <a href="#" onClick={(e) => {
        e.preventDefault();
        navigateToSection(section.id);
      }}>Documentation</a>
      <span className="breadcrumb-separator">›</span>
      <a href={`#s=${section.id}`} onClick={(e) => {
        e.preventDefault();
        navigateToSection(section.id);
      }}>{section.title}</a>
      {currentSubsection && (
        <>
          <span className="breadcrumb-separator">›</span>
          <a href={`#s=${section.id}&ss=${subsectionId}`} onClick={(e) => {
            e.preventDefault();
            navigateToSubsection(section.id, subsectionId);
          }}>{currentSubsection.title}</a>
        </>
      )}
    </div>
  );
};

// Table of Contents component
const SectionTableOfContents = ({ section }) => {
  const { navigateToSubsection } = useDocumentation();
  
  return (
    <div className="section-overview">
      <h2>In this section</h2>
      <ul>
        {Object.values(section.subsections)
          .sort((a, b) => a.order - b.order)
          .map(subsection => (
            <li key={`toc-${subsection.id}`}>
              <a
                href={`#s=${section.id}&ss=${subsection.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToSubsection(section.id, subsection.id);
                }}
              >
                {subsection.title}
              </a>
            </li>
          ))}
      </ul>
    </div>
  );
};

// Main ContentRenderer component
const ContentRenderer = memo(function ContentRenderer({
  activeSectionId,
  activeSubsectionId
}) {
  const { contentRef } = useDocumentation();
  const currentSection = activeSectionId ? documentationData.sections[activeSectionId] : null;
  
  // Handle scrolling to subsections with useLayoutEffect for immediate execution
  useLayoutEffect(() => {
    if (contentRef.current && currentSection) {
      if (activeSubsectionId) {
        const element = document.getElementById(`${activeSectionId}-${activeSubsectionId}`);
        if (element) {
          // Immediate scroll without animation for better performance
          contentRef.current.scrollTo({
            top: element.offsetTop - 20,
            behavior: 'auto' // No animation to eliminate lag
          });
        }
      } else {
        // If no subsection, scroll to top
        contentRef.current.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }, [contentRef, activeSectionId, activeSubsectionId]);
  
  if (!currentSection) return <div>Select a section</div>;
  
  return (
    <div className="docs-section">
      <Breadcrumb
        section={currentSection}
        subsectionId={activeSubsectionId}
      />
      
      <h1>
        {currentSection.title}
        <CopyLinkButton sectionId={activeSectionId} />
      </h1>
      <p>{getSectionOverview(activeSectionId)}</p>
      
      {/* Table of Contents */}
      <SectionTableOfContents section={currentSection} />
      
      {/* Render all subsections */}
      {Object.values(currentSection.subsections)
        .sort((a, b) => a.order - b.order)
        .map(subsection => {
          const SubsectionComponent = subsection.component;
          return (
            <div
              key={`content-${activeSectionId}-${subsection.id}`}
              id={`${activeSectionId}-${subsection.id}`}
              className="docs-subsection"
            >
              <h2>
                {subsection.title}
                <CopyLinkButton sectionId={activeSectionId} subsectionId={subsection.id} />
              </h2>
              {SubsectionComponent && <SubsectionComponent />}
            </div>
          );
        })}
    </div>
  );
});

export default ContentRenderer;